import { newUuid } from './newUuid';
/**
 * Wall Creation Utilities
 *
 * Utilities for creating free-floating interior walls with proper
 * structure wall + room wall architecture.
 *
 * A free-floating wall consists of:
 * - 1 structure wall (4.5" thick physical geometry)
 * - 4 room walls (zero-thickness surfaces for texturing)
 */

// Constants
export const WALL_THICKNESS = 0.1143; // 4.5 inches in meters
export const DEFAULT_WALL_LENGTH = 1.22; // 4 feet in meters
export const DEFAULT_WALL_HEIGHT = 2.4; // ~8 feet in meters

/**
 * Rotate a point around the Y axis by a given angle
 * Uses the same rotation convention as apply3DRotationTo2D in geometryUtils.js
 * @param {Object} point - Point with x, y, z coordinates
 * @param {number} angle - Rotation angle in radians
 * @returns {Object} Rotated point
 */
const rotatePointY = (point, angle) => {
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  // Match the Y rotation formula from apply3DRotationTo2D:
  // x' = x * cos + z * sin
  // z' = -x * sin + z * cos
  return {
    x: point.x * cos + point.z * sin,
    y: point.y,
    z: -point.x * sin + point.z * cos,
  };
};

/**
 * Add two 3D vectors
 */
const addVectors = (a, b) => ({
  x: a.x + b.x,
  y: a.y + b.y,
  z: a.z + b.z,
});

/**
 * Calculate bottom corners for a wall (used for alignment data)
 * @param {Object} center - Wall center position
 * @param {number} length - Wall length (dimensions.x)
 * @param {number} height - Wall height (dimensions.y)
 * @param {number} rotationY - Y rotation in radians
 * @returns {Object} bottomLeft, bottomRight, bottomMiddle positions
 */
export const calculateWallBottomCorners = (
  center,
  length,
  height,
  rotationY
) => {
  const halfLength = length / 2;
  const bottomY = center.y - height / 2;

  // Local space offsets (before rotation)
  const leftOffset = { x: -halfLength, y: 0, z: 0 };
  const rightOffset = { x: halfLength, y: 0, z: 0 };

  // Rotate offsets
  const rotatedLeft = rotatePointY(leftOffset, rotationY);
  const rotatedRight = rotatePointY(rightOffset, rotationY);

  const bottomLeft = {
    x: center.x + rotatedLeft.x,
    y: bottomY,
    z: center.z + rotatedLeft.z,
  };

  const bottomRight = {
    x: center.x + rotatedRight.x,
    y: bottomY,
    z: center.z + rotatedRight.z,
  };

  const bottomMiddle = {
    x: center.x,
    y: bottomY,
    z: center.z,
  };

  return { bottomLeft, bottomRight, bottomMiddle };
};

/**
 * Create a structure wall object
 * @param {Object} params - Wall parameters
 * @param {Object} params.center - Center position {x, y, z}
 * @param {number} params.length - Wall length in meters
 * @param {number} params.height - Wall height in meters
 * @param {number} params.rotationY - Y rotation in radians
 * @param {number} [params.story] - the storey it stands on; stamped when a
 *   number (an invented `story: 0` is what the storey filters exist to avoid)
 * @param {number} [params.floorY] - the floor it stands on; when given the
 *   wall's centre is `floorY + height / 2` and `center.y` is ignored
 * @returns {Object} Structure wall object ready for Firestore
 */
export const createStructureWall = ({
  center,
  length,
  height,
  rotationY,
  story,
  floorY,
}) => {
  const id = `sw_${newUuid()}`;
  const centerY = Number.isFinite(floorY) ? floorY + height / 2 : center.y;

  return {
    id,
    ...(Number.isFinite(story) ? { story } : {}),
    center: {
      x: center.x,
      y: centerY,
      z: center.z,
    },
    dimensions: {
      x: length,
      y: height,
      z: WALL_THICKNESS,
    },
    rotation: {
      x: 0,
      y: rotationY,
      z: 0,
    },
    isUserCreated: true,
    createdAt: new Date().toISOString(),
  };
};

/**
 * Create room walls for all 4 faces of a structure wall
 * @param {Object} structureWall - The structure wall object
 * @param {Object} options - Options for wall creation
 * @param {string} options.snappedEndpoint - Which endpoint was snapped: 'start' (skip left cap), 'end' (skip right cap) or 'both'
 * @returns {Array} Array of room wall objects (2-4 depending on snapped endpoints)
 */
export const createRoomWallsForStructureWall = (
  structureWall,
  options = {}
) => {
  const { id: structureWallId, center, dimensions, rotation } = structureWall;
  const { x: length, y: height, z: thickness } = dimensions;
  const rotationY = rotation.y;

  // 'start' / 'end' — that end meets another wall, so its cap would sit on
  // that wall's face; 'both' — a wall joined at both ends (the middle of a
  // drawn chain).
  const { snappedEndpoint } = options;
  const skipStartCap =
    snappedEndpoint === 'start' || snappedEndpoint === 'both';
  const skipEndCap = snappedEndpoint === 'end' || snappedEndpoint === 'both';

  const halfThickness = thickness / 2;
  const halfLength = length / 2;

  // Calculate positions for each face
  // Front face: +Z direction (before rotation)
  const frontOffset = rotatePointY({ x: 0, y: 0, z: halfThickness }, rotationY);
  const frontCenter = addVectors(center, frontOffset);

  // Back face: -Z direction (before rotation)
  const backOffset = rotatePointY({ x: 0, y: 0, z: -halfThickness }, rotationY);
  const backCenter = addVectors(center, backOffset);

  // Left cap: -X direction (before rotation)
  const leftOffset = rotatePointY({ x: -halfLength, y: 0, z: 0 }, rotationY);
  const leftCenter = addVectors(center, leftOffset);

  // Right cap: +X direction (before rotation)
  const rightOffset = rotatePointY({ x: halfLength, y: 0, z: 0 }, rotationY);
  const rightCenter = addVectors(center, rightOffset);

  // Helper to create a room wall
  const createRoomWall = (faceType, faceCenter, faceLength, faceRotationY) => {
    const corners = calculateWallBottomCorners(
      faceCenter,
      faceLength,
      height,
      faceRotationY
    );

    // Swap bottomLeft/bottomRight for back and right faces so that the
    // direction (bottomLeft → bottomRight) produces an outward-pointing normal
    // via perpDir = [-wallDir[1], wallDir[0]].
    const flip = faceType === 'back' || faceType === 'right';

    return {
      // A chain makes several walls in one tick: an id from the clock alone
      // repeats across them.
      id: `rw_${newUuid()}_${faceType}`,
      center: {
        x: faceCenter.x,
        y: faceCenter.y,
        z: faceCenter.z,
      },
      dimensions: {
        x: faceLength,
        y: height,
        z: 0, // Zero thickness for room walls
      },
      rotation: {
        x: 0,
        y: faceRotationY,
        z: 0,
      },
      alignment: {
        structureWallId,
        bottomLeft: flip ? corners.bottomRight : corners.bottomLeft,
        bottomRight: flip ? corners.bottomLeft : corners.bottomRight,
        bottomMiddle: corners.bottomMiddle,
      },
      isUserCreated: true,
      createdAt: new Date().toISOString(),
    };
  };

  // End caps have rotation perpendicular to main wall (+90 degrees)
  const capRotationY = rotationY + Math.PI / 2;

  // At a mitred end (a drawn chain's bend), the BACK face — local -Z, or +Z
  // where `frontFacesSameDirection` is false — runs on past the end by
  // `thickness × tan(miter)` to where the two outside faces meet, exactly as
  // the wall's outline and 3D body do (`calculateWallFootprint`). Cut square
  // at the tap instead, the room's outside faces stopped half a thickness
  // short of the corner on each side: a full-height notch.
  const leftRun = thickness * Math.tan(structureWall.leftMiter || 0);
  const rightRun = thickness * Math.tan(structureWall.rightMiter || 0);
  const mitred = (faceCenter) =>
    addVectors(
      faceCenter,
      rotatePointY({ x: (rightRun - leftRun) / 2, y: 0, z: 0 }, rotationY)
    );
  const backIsPlusZ = structureWall.frontFacesSameDirection === false;
  const faceOf = (faceType, faceCenter, sheared) =>
    sheared
      ? createRoomWall(
          faceType,
          mitred(faceCenter),
          length + leftRun + rightRun,
          rotationY
        )
      : createRoomWall(faceType, faceCenter, length, rotationY);

  const roomWalls = [
    // Main faces (same rotation as structure wall) - always included
    faceOf('front', frontCenter, backIsPlusZ),
    faceOf('back', backCenter, !backIsPlusZ),
  ];

  // Only add left cap if the 'start' endpoint wasn't snapped to another wall
  if (!skipStartCap) {
    roomWalls.push(createRoomWall('left', leftCenter, thickness, capRotationY));
  }

  // Only add right cap if the 'end' endpoint wasn't snapped to another wall
  if (!skipEndCap) {
    roomWalls.push(
      createRoomWall('right', rightCenter, thickness, capRotationY)
    );
  }

  return roomWalls;
};

/**
 * Create a complete free-floating wall with structure wall and room walls
 * This is a pure function that creates the wall objects - does not save to Firestore
 *
 * @param {Object} params - Wall parameters
 * @param {Object} params.center - Center position {x, y, z}
 * @param {number} params.length - Wall length in meters (default: 4ft)
 * @param {number} params.height - Wall height in meters (default: 8ft)
 * @param {number} params.rotationY - Y rotation in radians (default: 0)
 * @returns {Object} { structureWall, roomWalls }
 */
export const createFreeFloatingWallObjects = ({
  center,
  length = DEFAULT_WALL_LENGTH,
  height = DEFAULT_WALL_HEIGHT,
  rotationY = 0,
}) => {
  // Create structure wall
  const structureWall = createStructureWall({
    center,
    length,
    height,
    rotationY,
  });

  // Create room walls for all 4 faces
  const roomWalls = createRoomWallsForStructureWall(structureWall);

  return {
    structureWall,
    roomWalls,
  };
};

/**
 * Get the floor polygon points for a room (in [x, z] format for point-in-polygon test)
 * @param {Object} room - Room object with floors array
 * @returns {Array|null} Array of [x, z] points or null if no polygon found
 */
const getRoomFloorPolygon = (room) => {
  if (!room?.floors?.[0]?.polygonCorners) {
    return null;
  }
  return room.floors[0].polygonCorners.map((corner) => [corner.x, corner.z]);
};

/**
 * Check if a point (x, z) is inside a room's floor polygon
 * Uses ray casting algorithm
 * @param {number} x - X coordinate
 * @param {number} z - Z coordinate
 * @param {Array} polygonPoints - Array of [x, z] polygon points
 * @returns {boolean} True if point is inside
 */
const isPointInPolygon = (x, z, polygonPoints) => {
  if (!polygonPoints || polygonPoints.length < 3) return false;

  let inside = false;
  for (
    let i = 0, j = polygonPoints.length - 1;
    i < polygonPoints.length;
    j = i++
  ) {
    const [xi, zi] = polygonPoints[i];
    const [xj, zj] = polygonPoints[j];

    const intersect =
      zi > z !== zj > z && x < ((xj - xi) * (z - zi)) / (zj - zi) + xi;

    if (intersect) inside = !inside;
  }

  return inside;
};

/**
 * Find which room a point is inside
 * @param {number} x - X coordinate
 * @param {number} z - Z coordinate
 * @param {Array} rooms - Array of room objects
 * @returns {Object|null} The room the point is inside, or null
 */
export const findRoomContainingPoint = (x, z, rooms) => {
  for (const room of rooms) {
    const polygon = getRoomFloorPolygon(room);
    if (polygon && isPointInPolygon(x, z, polygon)) {
      return room;
    }
  }
  return null;
};

/**
 * Inspect a room's walls and report the typical wall-top height in
 * meters. Used when creating a new partition wall inside an existing
 * room so the wall reaches the ceiling instead of stopping at the
 * legacy 8' default.
 *
 * Uses the mode (most common value, rounded to the millimeter) of
 * `wall.dimensions.y` across all valid walls so a single tall gable
 * peak or a degenerate wall can't drag the answer off. Returns
 * `fallback` when the room has no usable walls.
 */
export const getRoomWallHeight = (room, fallback = DEFAULT_WALL_HEIGHT) => {
  const walls = room?.walls;
  if (!Array.isArray(walls) || walls.length === 0) return fallback;
  const heights = walls
    .map((w) => w?.dimensions?.y)
    .filter((h) => typeof h === 'number' && h > 0.1);
  if (heights.length === 0) return fallback;
  const counts = new Map();
  for (const h of heights) {
    const key = Math.round(h * 1000); // millimeter buckets
    counts.set(key, (counts.get(key) || 0) + 1);
  }
  let bestKey = null;
  let bestCount = -1;
  for (const [key, count] of counts) {
    if (count > bestCount) {
      bestCount = count;
      bestKey = key;
    }
  }
  return bestKey != null ? bestKey / 1000 : fallback;
};

/**
 * Look up the ceiling height (= wall top height) at a given XZ
 * coordinate by finding the host room and inspecting its walls.
 * Returns `fallback` when the point isn't inside any room (the user
 * is drawing in a hallway between rooms or in empty space).
 */
export const getCeilingHeightAtPoint = (
  x,
  z,
  rooms,
  fallback = DEFAULT_WALL_HEIGHT
) => {
  if (!Array.isArray(rooms) || rooms.length === 0) return fallback;
  const host = findRoomContainingPoint(x, z, rooms);
  return host ? getRoomWallHeight(host, fallback) : fallback;
};

/**
 * Find intersection point of two line segments
 * @param {Array} p1 - Start of segment 1 [x, z]
 * @param {Array} p2 - End of segment 1 [x, z]
 * @param {Array} p3 - Start of segment 2 [x, z]
 * @param {Array} p4 - End of segment 2 [x, z]
 * @returns {Array|null} Intersection point [x, z] or null if no intersection
 */
const lineSegmentIntersection = (p1, p2, p3, p4) => {
  const [x1, y1] = p1;
  const [x2, y2] = p2;
  const [x3, y3] = p3;
  const [x4, y4] = p4;

  const denom = (x1 - x2) * (y3 - y4) - (y1 - y2) * (x3 - x4);
  if (Math.abs(denom) < 1e-10) return null; // Parallel lines

  const t = ((x1 - x3) * (y3 - y4) - (y1 - y3) * (x3 - x4)) / denom;
  const u = -((x1 - x2) * (y1 - y3) - (y1 - y2) * (x1 - x3)) / denom;

  if (t >= 0 && t <= 1 && u >= 0 && u <= 1) {
    return [x1 + t * (x2 - x1), y1 + t * (y2 - y1)];
  }
  return null;
};

// Minimum length for a room wall segment (in meters) - about 1 inch
const MIN_ROOM_WALL_LENGTH = 0.025;

/**
 * Calculate parameter t for a point along a segment
 * t=0 at segStart, t=1 at segEnd
 * @param {Array} point - Point [x, z]
 * @param {Array} segStart - Segment start [x, z]
 * @param {Array} segEnd - Segment end [x, z]
 * @returns {number} Parameter t
 */
const calculateParameterT = (point, segStart, segEnd) => {
  const dx = segEnd[0] - segStart[0];
  const dz = segEnd[1] - segStart[1];

  // Use the component with larger magnitude to avoid division by near-zero
  if (Math.abs(dx) > Math.abs(dz)) {
    return (point[0] - segStart[0]) / dx;
  } else if (Math.abs(dz) > 1e-10) {
    return (point[1] - segStart[1]) / dz;
  }
  return 0;
};

/**
 * Clip a line segment to a polygon, returning the portion inside
 * @param {Array} segStart - Start of segment [x, z]
 * @param {Array} segEnd - End of segment [x, z]
 * @param {Array} polygon - Array of polygon points [[x, z], ...]
 * @returns {Object|null} { start: [x,z], end: [x,z] } or null if entirely outside
 */
const clipSegmentToPolygon = (segStart, segEnd, polygon) => {
  if (!polygon || polygon.length < 3) return null;

  // Collect all intersection points with polygon edges
  const intersections = [];

  for (let i = 0; i < polygon.length; i++) {
    const j = (i + 1) % polygon.length;
    const edgeStart = polygon[i];
    const edgeEnd = polygon[j];

    const intersection = lineSegmentIntersection(
      segStart,
      segEnd,
      edgeStart,
      edgeEnd
    );
    if (intersection) {
      const t = calculateParameterT(intersection, segStart, segEnd);
      // Only include intersections that are on the segment (t between 0 and 1)
      if (t >= -1e-10 && t <= 1 + 1e-10) {
        intersections.push({
          t: Math.max(0, Math.min(1, t)),
          point: intersection,
        });
      }
    }
  }

  // Check if endpoints are inside
  const startInside = isPointInPolygon(segStart[0], segStart[1], polygon);
  const endInside = isPointInPolygon(segEnd[0], segEnd[1], polygon);

  // Sort intersections by parameter t
  intersections.sort((a, b) => a.t - b.t);

  let clippedStart, clippedEnd;

  if (startInside && endInside) {
    // Entire segment inside
    clippedStart = segStart;
    clippedEnd = segEnd;
  } else if (startInside && !endInside && intersections.length >= 1) {
    // Start inside, exits polygon at first intersection
    clippedStart = segStart;
    clippedEnd = intersections[0].point;
  } else if (!startInside && endInside && intersections.length >= 1) {
    // End inside, enters polygon at last intersection before end
    clippedStart = intersections[intersections.length - 1].point;
    clippedEnd = segEnd;
  } else if (!startInside && !endInside && intersections.length >= 2) {
    // Both outside but segment passes through polygon
    // Use first entry and first exit
    clippedStart = intersections[0].point;
    clippedEnd = intersections[1].point;
  } else {
    return null; // Entirely outside
  }

  // Check minimum length
  const clippedLength = Math.sqrt(
    Math.pow(clippedEnd[0] - clippedStart[0], 2) +
      Math.pow(clippedEnd[1] - clippedStart[1], 2)
  );

  if (clippedLength < MIN_ROOM_WALL_LENGTH) {
    return null; // Too short, likely numerical artifact
  }

  return { start: clippedStart, end: clippedEnd };
};

/**
 * Calculate face data for a structure wall (positions and dimensions for each face)
 * @param {Object} structureWall - The structure wall object
 * @returns {Array} Array of face objects with faceType, center, length, rotationY
 */
const calculateStructureWallFaces = (structureWall) => {
  const { center, dimensions, rotation } = structureWall;
  const { x: length, y: height, z: thickness } = dimensions;
  const rotationY = rotation.y;

  const halfThickness = thickness / 2;
  const halfLength = length / 2;

  // Calculate positions for each face
  const frontOffset = rotatePointY({ x: 0, y: 0, z: halfThickness }, rotationY);
  const backOffset = rotatePointY({ x: 0, y: 0, z: -halfThickness }, rotationY);
  const leftOffset = rotatePointY({ x: -halfLength, y: 0, z: 0 }, rotationY);
  const rightOffset = rotatePointY({ x: halfLength, y: 0, z: 0 }, rotationY);

  const capRotationY = rotationY + Math.PI / 2;

  return [
    {
      faceType: 'front',
      center: addVectors(center, frontOffset),
      length: length,
      rotationY: rotationY,
    },
    {
      faceType: 'back',
      center: addVectors(center, backOffset),
      length: length,
      rotationY: rotationY,
    },
    {
      faceType: 'left',
      center: addVectors(center, leftOffset),
      length: thickness,
      rotationY: capRotationY,
    },
    {
      faceType: 'right',
      center: addVectors(center, rightOffset),
      length: thickness,
      rotationY: capRotationY,
    },
  ];
};

/**
 * Calculate the endpoints of a wall face (as a line segment in X-Z plane)
 * @param {Object} faceCenter - Center of the face {x, y, z}
 * @param {number} faceLength - Length of the face
 * @param {number} rotationY - Rotation around Y axis
 * @returns {Object} { left: [x, z], right: [x, z] }
 */
const calculateFaceEndpoints = (faceCenter, faceLength, rotationY) => {
  const halfLength = faceLength / 2;
  const cos = Math.cos(rotationY);
  const sin = Math.sin(rotationY);

  // Wall extends along its local X axis, rotated by rotationY: (cos, -sin),
  // the `rotatePointY` convention calculateWallBottomCorners uses. A `+sin`
  // here clips a diagonal wall's faces along the mirror-image line.
  return {
    left: [faceCenter.x - cos * halfLength, faceCenter.z + sin * halfLength],
    right: [faceCenter.x + cos * halfLength, faceCenter.z - sin * halfLength],
  };
};

/**
 * Create a room wall from clipped segment data
 * @param {Object} params - Parameters for creating the room wall
 * @returns {Object} Room wall object
 */
const createRoomWallFromSegment = ({
  id,
  faceType,
  clippedStart,
  clippedEnd,
  centerY,
  height,
  rotationY,
  structureWallId,
  timestamp,
}) => {
  // Calculate center of clipped segment
  const clippedCenterX = (clippedStart[0] + clippedEnd[0]) / 2;
  const clippedCenterZ = (clippedStart[1] + clippedEnd[1]) / 2;

  // Calculate length of clipped segment
  const clippedLength = Math.sqrt(
    Math.pow(clippedEnd[0] - clippedStart[0], 2) +
      Math.pow(clippedEnd[1] - clippedStart[1], 2)
  );

  const clippedCenter = { x: clippedCenterX, y: centerY, z: clippedCenterZ };

  // Calculate corners for alignment data
  const corners = calculateWallBottomCorners(
    clippedCenter,
    clippedLength,
    height,
    rotationY
  );

  // Swap bottomLeft/bottomRight for back and right faces so that the
  // direction (bottomLeft → bottomRight) produces an outward-pointing normal
  const flip = faceType === 'back' || faceType === 'right';

  return {
    id,
    center: {
      x: clippedCenter.x,
      y: clippedCenter.y,
      z: clippedCenter.z,
    },
    dimensions: {
      x: clippedLength,
      y: height,
      z: 0, // Zero thickness for room walls
    },
    rotation: {
      x: 0,
      y: rotationY,
      z: 0,
    },
    alignment: {
      structureWallId,
      bottomLeft: flip ? corners.bottomRight : corners.bottomLeft,
      bottomRight: flip ? corners.bottomLeft : corners.bottomRight,
      bottomMiddle: corners.bottomMiddle,
    },
    isUserCreated: true,
    createdAt: new Date().toISOString(),
  };
};

/**
 * Create room walls for a structure wall, placing them only in rooms where faces are inside.
 * Clips faces that span multiple rooms so each room gets the correct portion.
 * Returns a map of roomId -> array of room walls for that room.
 *
 * @param {Object} structureWall - The structure wall object
 * @param {Array} rooms - Array of room objects with floors[0].polygonCorners
 * @returns {Map<string, Array>} Map of roomId to array of room walls
 */
export const createRoomWallsInsideRooms = (structureWall, rooms) => {
  const { id: structureWallId, center, dimensions } = structureWall;
  const height = dimensions.y;
  const centerY = center.y;
  const timestamp = Date.now();

  console.log('🔍 createRoomWallsInsideRooms called');
  console.log('  Structure wall center:', center);
  console.log('  Structure wall dimensions:', dimensions);
  console.log('  Number of rooms:', rooms.length);

  // Get all faces of the structure wall
  const faces = calculateStructureWallFaces(structureWall);

  console.log(
    '  Faces calculated:',
    faces.map((f) => ({
      faceType: f.faceType,
      center: { x: f.center.x.toFixed(3), z: f.center.z.toFixed(3) },
      length: f.length.toFixed(3),
    }))
  );

  // Map of roomId -> room walls
  const roomWallsByRoom = new Map();

  let wallCounter = 0;

  for (const face of faces) {
    const {
      faceType,
      center: faceCenter,
      length: faceLength,
      rotationY,
    } = face;

    // Calculate endpoints of this face as a line segment
    const endpoints = calculateFaceEndpoints(faceCenter, faceLength, rotationY);

    console.log(`  Face "${faceType}":`, {
      faceCenter: {
        x: faceCenter.x.toFixed(3),
        z: faceCenter.z.toFixed(3),
      },
      endpoints: {
        left: [endpoints.left[0].toFixed(3), endpoints.left[1].toFixed(3)],
        right: [endpoints.right[0].toFixed(3), endpoints.right[1].toFixed(3)],
      },
    });

    // For each room, check if this face (or part of it) is inside
    for (const room of rooms) {
      const polygon = getRoomFloorPolygon(room);
      if (!polygon) {
        console.log(`    Room ${room.id}: no polygon`);
        continue;
      }

      // Debug: show first few polygon points and test center point
      const polyPreview = polygon
        .slice(0, 4)
        .map((p) => `[${p[0].toFixed(2)},${p[1].toFixed(2)}]`)
        .join(', ');
      const faceCenterInside = isPointInPolygon(
        faceCenter.x,
        faceCenter.z,
        polygon
      );
      console.log(
        `    Room ${room.id.substring(0, 8)}: polygon (${polygon.length} pts): ${polyPreview}..., faceCenterInside: ${faceCenterInside}`
      );

      // Clip the face segment to this room's polygon
      const clipped = clipSegmentToPolygon(
        endpoints.left,
        endpoints.right,
        polygon
      );

      if (clipped) {
        const clippedLength = Math.sqrt(
          Math.pow(clipped.end[0] - clipped.start[0], 2) +
            Math.pow(clipped.end[1] - clipped.start[1], 2)
        );
        console.log(`    Room ${room.id}: CLIPPED`, {
          start: [clipped.start[0].toFixed(3), clipped.start[1].toFixed(3)],
          end: [clipped.end[0].toFixed(3), clipped.end[1].toFixed(3)],
          length: clippedLength.toFixed(3),
        });

        // Create a room wall for the clipped portion
        const roomWall = createRoomWallFromSegment({
          id: `rw_${newUuid()}_${faceType}_${wallCounter++}`,
          faceType,
          clippedStart: clipped.start,
          clippedEnd: clipped.end,
          centerY,
          height,
          rotationY,
          structureWallId,
          timestamp,
        });

        // Debug: show the created room wall's center and dimensions
        console.log(`    Created room wall:`, {
          id: roomWall.id,
          center: {
            x: roomWall.center.x.toFixed(3),
            z: roomWall.center.z.toFixed(3),
          },
          dimensions: {
            x: roomWall.dimensions.x.toFixed(3),
          },
        });

        // Add to the room's wall list
        const roomId = room.id;
        if (!roomWallsByRoom.has(roomId)) {
          roomWallsByRoom.set(roomId, []);
        }
        roomWallsByRoom.get(roomId).push(roomWall);
      } else {
        console.log(`    Room ${room.id}: not inside`);
      }
    }
  }

  console.log('  Total room walls created:', wallCounter);
  for (const [roomId, walls] of roomWallsByRoom) {
    console.log(`    Room ${roomId}: ${walls.length} walls`);
  }

  return roomWallsByRoom;
};
